const configuredLiveSeasonYear = Number(process.env.REACT_APP_CURRENT_SEASON_YEAR);

/** Current live season (omit `year` on the API for this value). */
export const SCORECARD_LIVE_SEASON_YEAR =
  Number.isFinite(configuredLiveSeasonYear) && configuredLiveSeasonYear > 0
    ? configuredLiveSeasonYear
    : 2026;

export const DEFAULT_SCORECARD_SEASON_YEARS = [
  SCORECARD_LIVE_SEASON_YEAR,
  SCORECARD_LIVE_SEASON_YEAR - 1,
  SCORECARD_LIVE_SEASON_YEAR - 2,
];

const MIN_SEASON_YEAR = 2000;

const isPlausibleSeasonYear = (year) =>
  Number.isFinite(year) && year >= MIN_SEASON_YEAR && year <= SCORECARD_LIVE_SEASON_YEAR + 1;

/** Scalars must be wrapped — iterating a string like `"2026"` yields `2`, `0`, `6`. */
const toYearValueList = (input) => {
  if (input == null) {
    return [];
  }
  if (Array.isArray(input)) {
    return input;
  }
  return [input];
};

export const normalizeSeasonYear = (year) => {
  const parsed = Number(year);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return SCORECARD_LIVE_SEASON_YEAR;
};

export const isLiveSeasonYear = (year) => normalizeSeasonYear(year) === SCORECARD_LIVE_SEASON_YEAR;

const normalizeScorecardRole = (role) =>
  String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

/** Scorecard roles that may use the tannery filter on the live season. */
export const canFilterScorecardByTannery = (userRole) => {
  const role = normalizeScorecardRole(userRole);
  return (
    role === "SUPER_ADMIN" ||
    role === "EXECUTIVE" ||
    role === "DATA_ENTRY" ||
    role === "DATAENTRY" ||
    role === "TANNERY_ADMIN"
  );
};

const parseAuthTokenPayload = (token) => {
  try {
    const segment = String(token || "").split(".")[1];
    if (!segment) {
      return null;
    }
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "="));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const getTanneryIdFromAuthToken = (token) => {
  const payload = parseAuthTokenPayload(token);
  const tanneryId = payload?.tannery_id ?? payload?.tanneryId;
  return tanneryId != null && String(tanneryId).trim() ? String(tanneryId).trim() : "";
};

/**
 * Super Admin (no tannery filter) uses `/scorecard/super-admin`; all other scorecard
 * roles use `/scorecard` (Executive, Data Entry, Tannery Admin, and filtered Super Admin).
 */
export const getScorecardEndpoint = (userRole, { tanneryId } = {}) => {
  const role = normalizeScorecardRole(userRole);
  const hasTanneryFilter =
    tanneryId && String(tanneryId).trim() && String(tanneryId) !== "all";

  if (hasTanneryFilter || role !== "SUPER_ADMIN") {
    return "/scorecard";
  }
  return "/scorecard/super-admin";
};

const parseTanneryListPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

/** Loads tannery dropdown options for the scorecard filter. */
export const fetchScorecardTanneryFilterOptions = async (baseApiUrl, token, userRole) => {
  const base = String(baseApiUrl || "").replace(/\/+$/, "");
  if (!base || !token) {
    return null;
  }

  const role = normalizeScorecardRole(userRole);
  if (role === "TANNERY_ADMIN") {
    const tanneryId = getTanneryIdFromAuthToken(token);
    if (!tanneryId) {
      return null;
    }
    return [{ value: tanneryId, label: "My Tannery" }];
  }

  const response = await fetch(`${base}/super-admin/tanneries`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    return null;
  }

  const payload = await response.json();
  return parseTanneryListPayload(payload)
    .map((item) => ({
      value: String(item?.id ?? item?.tannery_id ?? "").trim(),
      label: String(item?.tannery_name ?? item?.name ?? "").trim(),
    }))
    .filter((item) => item.value && item.label)
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const mergeTanneryFilterOptions = (existingOptions, additionalOptions) => {
  const merged = [...(existingOptions || []), ...(additionalOptions || [])].filter(
    (item) => item && item.value && item.label
  );
  const uniqueByValue = Array.from(new Map(merged.map((item) => [item.value, item])).values());
  const sorted = uniqueByValue
    .filter((item) => item.value !== "all")
    .sort((a, b) => a.label.localeCompare(b.label));
  return [{ value: "all", label: "All Tanneries" }, ...sorted];
};

const SCORECARD_EID_DAY_API_LABELS = {
  EID_DAY_1: "Eid Day 1",
  EID_DAY_2: "Eid Day 2",
  EID_DAY_3: "Eid Day 3",
  EID_DAY_4: "Eid Day 4",
};

/** Scorecard API expects labels like `Eid Day 1`, not `EID_DAY_1`. */
export const normalizeScorecardEidDay = (eidDayValue) => {
  if (!eidDayValue || eidDayValue === "all") {
    return "all";
  }
  return SCORECARD_EID_DAY_API_LABELS[eidDayValue] || String(eidDayValue).trim();
};

export const buildScorecardQueryParams = ({ eidDay, tanneryId, year, zila, userRole }) => {
  const queryParams = new URLSearchParams();
  const seasonYear = normalizeSeasonYear(year);

  if (!isLiveSeasonYear(seasonYear)) {
    queryParams.set("year", String(seasonYear));
  }

  const scorecardEidDay = normalizeScorecardEidDay(eidDay);
  if (scorecardEidDay && scorecardEidDay !== "all") {
    queryParams.set("eid_day", scorecardEidDay);
  } else if (eidDay && String(eidDay).match(/^\d+$/)) {
    queryParams.set("eid_day", String(eidDay).trim());
  }

  if (
    isLiveSeasonYear(seasonYear) &&
    canFilterScorecardByTannery(userRole) &&
    tanneryId &&
    tanneryId !== "all"
  ) {
    queryParams.set("tannery_id", tanneryId);
  }

  const zilaName = String(zila || "").trim();
  if (zilaName && zilaName !== "all") {
    queryParams.set("zila", zilaName);
  }

  return queryParams;
};

export const getScorecardQueryString = (params) => {
  const queryString = buildScorecardQueryParams(params).toString();
  return queryString ? `?${queryString}` : "";
};

export const extractScorecardData = (payload) =>
  payload?.data && typeof payload.data === "object" ? payload.data : payload;

/** First matching row from `zila_wise` (or the only row when the API returns one). */
export const findZilaWiseItem = (data, zilaName) => {
  const section = data?.zila_wise || data?.zilaWise;
  if (!Array.isArray(section) || section.length === 0) {
    return null;
  }

  const target = String(zilaName || "").trim().toLowerCase();
  if (target && target !== "all") {
    const match = section.find((item) => {
      const name = String(item?.zila ?? item?.zila_name ?? item?.label ?? "")
        .trim()
        .toLowerCase();
      return name === target;
    });
    if (match) {
      return match;
    }
  }

  return section.length === 1 ? section[0] : null;
};

/** Root `previous_year`, `total_comparison.previous_year`, or zila `comparison.previous_year`. */
export const getComparisonYearFromScorecardData = (data, { zila } = {}) => {
  const zilaItem = zila ? findZilaWiseItem(data, zila) : null;
  const zilaComparison = zilaItem?.comparison;
  if (zilaComparison && typeof zilaComparison === "object") {
    const zilaYear = Number(zilaComparison.previous_year);
    if (Number.isFinite(zilaYear) && zilaYear > 0) {
      return zilaYear;
    }
  }

  const rootYear = Number(data?.previous_year);
  if (Number.isFinite(rootYear) && rootYear > 0) {
    return rootYear;
  }

  const comparison = data?.total_comparison;
  if (!comparison || typeof comparison !== "object") {
    return null;
  }
  const year = Number(comparison.previous_year);
  return Number.isFinite(year) && year > 0 ? year : null;
};

export const extractAvailableYears = (payload) => {
  const sources = [payload, payload?.data, payload?.error];
  for (const source of sources) {
    if (!source || typeof source !== "object") {
      continue;
    }
    const years = source.available_years;
    if (!Array.isArray(years) || years.length === 0) {
      continue;
    }
    return [...new Set(years.map((value) => Number(value)).filter(isPlausibleSeasonYear))].sort(
      (a, b) => b - a
    );
  }
  return [];
};

export const mergeSeasonYearOptions = (existingYears, ...yearLists) => {
  const merged = new Set(
    toYearValueList(existingYears)
      .map((value) => Number(value))
      .filter(isPlausibleSeasonYear)
  );
  for (const list of yearLists) {
    for (const value of toYearValueList(list)) {
      const year = Number(value);
      if (isPlausibleSeasonYear(year)) {
        merged.add(year);
      }
    }
  }
  return [...merged].sort((a, b) => b - a);
};

export const getScorecardErrorMessage = (payload, fallbackMessage) => {
  if (!payload || typeof payload !== "object") {
    return fallbackMessage;
  }
  const baseMessage = payload.message || payload.detail || fallbackMessage;
  const availableYears = extractAvailableYears(payload);
  if (availableYears.length === 0) {
    return baseMessage;
  }
  return `${baseMessage} Available years: ${availableYears.join(", ")}.`;
};

export const getPreviousYearColumnLabel = (comparisonYear) =>
  comparisonYear ? String(comparisonYear) : "Prev Year";

export const formatSeasonYearOptionLabel = (year) => {
  const normalized = normalizeSeasonYear(year);
  return isLiveSeasonYear(normalized) ? `${normalized} (Live)` : String(normalized);
};

/** Maps UI Eid day values (`EID_DAY_1`, etc.) to export API values (`1`, `all`). */
export const normalizeEidDayForExport = (eidDayValue) => {
  if (eidDayValue === "all") {
    return "all";
  }
  const match = String(eidDayValue).match(/(\d+)$/);
  return match ? match[1] : "all";
};

export const isTanneryWiseExport = (tanneryId) =>
  Boolean(tanneryId && String(tanneryId).trim() && String(tanneryId) !== "all");

export const getScorecardExportEndpoint = (tanneryId) =>
  isTanneryWiseExport(tanneryId)
    ? "/scorecard/tannery-wise/export"
    : "/scorecard/zila-wise/export";

export const buildScorecardExportQueryParams = ({ format, language, eidDay, tanneryId }) => {
  const params = new URLSearchParams({
    format: format || "csv",
    language: language || "en",
    eid_day: normalizeEidDayForExport(eidDay),
  });

  if (isTanneryWiseExport(tanneryId)) {
    params.set("tannery_id", String(tanneryId).trim());
  }

  return params;
};
